/**
 * CarrierCombobox Component
 * Searchable dropdown for selecting or creating carriers
 * 
 * Features:
 * - Search existing carriers with real-time filtering
 * - Create new carriers inline
 * - Case-insensitive duplicate detection
 * 
 * @version 1.0.0
 * @date January 2026
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Check, ChevronsUpDown, Plus, Loader2, Truck, Search } from 'lucide-react';
import { cn } from '../lib/utils';
import { Button } from './ui/button';
import { Input } from './ui/input';
import api from '../services/api';
import { toast } from 'sonner';

const CarrierCombobox = ({ 
  value, 
  onChange, 
  placeholder = "Select carrier...",
  disabled = false 
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [carriers, setCarriers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Load carriers
  const loadCarriers = useCallback(async (searchTerm = '') => {
    setLoading(true);
    try {
      const params = searchTerm ? `?search=${encodeURIComponent(searchTerm)}` : '';
      const response = await api.get(`/api/v1/inventory/v2/carriers${params}`);
      setCarriers(response.data || []);
    } catch (error) {
      console.error('Error loading carriers:', error);
      // Fallback to empty array, don't show error toast for initial load
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    loadCarriers();
  }, [loadCarriers]);

  // Search with debounce
  useEffect(() => {
    const debounce = setTimeout(() => {
      if (open) {
        loadCarriers(search);
      }
    }, 300);
    return () => clearTimeout(debounce);
  }, [search, open, loadCarriers]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Check if search term already exists (case-insensitive)
  const searchExistsInCarriers = carriers.some(
    c => c.name.toLowerCase() === search.toLowerCase()
  );

  // Create new carrier
  const handleCreateCarrier = async () => {
    if (!search.trim()) return;
    
    setCreating(true);
    try {
      const response = await api.post('/api/v1/inventory/v2/carriers', {
        name: search.trim()
      });
      
      toast.success(`Carrier "${response.data.name}" created`);
      
      // Update value and close
      onChange(response.data.name);
      setSearch('');
      setOpen(false);
      
      // Refresh carriers list
      loadCarriers();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to create carrier';
      toast.error(errorMsg);
    } finally {
      setCreating(false);
    }
  };

  // Handle selection
  const handleSelect = (carrierName) => {
    onChange(carrierName);
    setSearch('');
    setOpen(false);
  };

  // Filter carriers based on search
  const filteredCarriers = carriers.filter(c => 
    c.name.toLowerCase().includes(search.toLowerCase())
  );

  // Show create option if search term doesn't match any existing carrier
  const showCreateOption = search.trim() && !searchExistsInCarriers && !loading;

  return (
    <div className="relative" ref={containerRef}>
      {/* Trigger Button */}
      <Button
        type="button"
        variant="outline"
        role="combobox"
        aria-expanded={open}
        className={cn(
          "w-full justify-between font-normal",
          !value && "text-muted-foreground"
        )}
        onClick={() => {
          if (!disabled) {
            setOpen(!open);
            if (!open) {
              setTimeout(() => inputRef.current?.focus(), 100);
            }
          }
        }}
        disabled={disabled}
        data-testid="carrier-combobox-trigger"
      >
        <span className="flex items-center gap-2 truncate">
          {value ? (
            <>
              <Truck className="h-4 w-4 text-gray-500" />
              {value}
            </>
          ) : (
            placeholder
          )}
        </span>
        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </Button>

      {/* Dropdown */}
      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md border bg-white shadow-lg">
          {/* Search Input */}
          <div className="p-2 border-b">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                ref={inputRef}
                placeholder="Search or type new carrier..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 h-9"
                data-testid="carrier-search-input"
              />
              {loading && (
                <Loader2 className="absolute right-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 animate-spin text-gray-400" />
              )}
            </div>
          </div>

          {/* Options List */}
          <div className="max-h-48 overflow-y-auto p-1">
            {/* Create New Option */}
            {showCreateOption && (
              <button
                type="button"
                className="w-full flex items-center gap-2 px-3 py-2 text-left rounded-sm hover:bg-blue-50 text-blue-600 border-b mb-1"
                onClick={handleCreateCarrier}
                disabled={creating}
                data-testid="carrier-create-btn"
              >
                {creating ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Plus className="h-4 w-4" />
                )}
                <span>Create "{search.trim()}"</span>
              </button>
            )}

            {/* Existing Carriers */}
            {filteredCarriers.length > 0 ? (
              filteredCarriers.map((carrier) => (
                <button
                  key={carrier.carrier_id}
                  type="button"
                  className={cn(
                    "w-full flex items-center gap-2 px-3 py-2 text-left rounded-sm hover:bg-gray-100",
                    value === carrier.name && "bg-blue-50"
                  )}
                  onClick={() => handleSelect(carrier.name)}
                  data-testid={`carrier-option-${carrier.carrier_id}`}
                >
                  <Check
                    className={cn(
                      "h-4 w-4",
                      value === carrier.name ? "opacity-100 text-blue-600" : "opacity-0"
                    )}
                  />
                  <Truck className="h-4 w-4 text-gray-400" />
                  <span>{carrier.name}</span>
                  {carrier.is_default && (
                    <span className="ml-auto text-xs text-gray-400">Default</span>
                  )}
                </button>
              ))
            ) : (
              !showCreateOption && (
                <div className="px-3 py-6 text-center text-sm text-gray-500">
                  {loading ? (
                    <div className="flex items-center justify-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading carriers...
                    </div>
                  ) : (
                    search ? 'No carriers found' : 'No carriers available'
                  )}
                </div>
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CarrierCombobox;
